import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { phone as phoneLib } from '@parsina/framework';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import { LOOKUP_PATIENTS_BY_PHONE } from './useDoctorBooking';

const NONE = Object.freeze([]);

/**
 * The records already on a mobile number, for the «بیمار جدید» form of ثبت نوبت.
 *
 * A mobile is a household — a mother books for three children on hers — so before a
 * new patient is made the doctor sees every record on the number and chooses: the
 * person in front of them is one of those rows, or someone new on the same number.
 *
 * Fed whatever the form holds (E.164 from `PhoneInput`) and it only asks once that
 * value PARSES, so a lookup fires when the number becomes valid, not on every
 * keystroke; the short delay absorbs a paste followed by a correction.
 *
 * What a row shows depends on the CLINIC (its own patients by name, anyone else
 * masked), so answers are cached per clinic AND number. A number whose lookup failed
 * is not cached and is asked again next time. A failed lookup is not a refusal:
 * `addPatient` checks the number again and answers PHONE_HAS_PATIENTS itself, which
 * `refresh` turns into the same list.
 */
export function usePhoneCandidates(raw, clinicId, { delay = 400, enabled = true } = {}) {
  const number = raw ? (phoneLib.normalizePhone(String(raw).trim()) || '') : '';
  const key = number && clinicId ? `${clinicId}|${number}` : '';
  const cache = useRef(new Map());
  const [, rerender] = useReducer((n) => n + 1, 0);
  const [loading, setLoading] = useState(false);
  // The number whose last lookup failed — said under the field, never blocking.
  const [failedKey, setFailedKey] = useState('');

  useEffect(() => {
    if (!enabled || !key || cache.current.has(key)) { setLoading(false); return undefined; }
    let live = true;
    setLoading(true);
    setFailedKey('');
    const timer = setTimeout(async () => {
      try {
        const data = await bffQuery(LOOKUP_PATIENTS_BY_PHONE, { phone: number, clinicId });
        cache.current.set(key, data?.lookupPatientsByPhone?.candidates || NONE);
      } catch {
        // Not cached, so the next visit to this number asks again.
        if (live) setFailedKey(key);
      } finally {
        if (live) { setLoading(false); rerender(); }
      }
    }, delay);
    return () => { live = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, delay]);

  /** Ask now, bypassing the cache — after the server refused a number we had cleared. */
  const refresh = useCallback(async () => {
    if (!key) return NONE;
    const data = await bffQuery(LOOKUP_PATIENTS_BY_PHONE, { phone: number, clinicId });
    const found = data?.lookupPatientsByPhone?.candidates || NONE;
    cache.current.set(key, found);
    setFailedKey('');
    rerender();
    return found;
  }, [key, number, clinicId]);

  return {
    number,
    candidates: (key && cache.current.get(key)) || NONE,
    loading: enabled && loading && !!key,
    failed: enabled && !loading && !!key && failedKey === key,
    refresh,
  };
}

export default usePhoneCandidates;
